import { useState, useEffect, useCallback, useRef } from 'react';
import { ffmpegProcessor, type ProgressCallback } from '../utils/ffmpeg';
import type { VideoClip, Filter } from '../types';

interface UseFFmpegReturn {
    isLoaded: boolean;
    isLoading: boolean;
    error: string | null;
    load: () => Promise<void>;
    trimVideo: (videoFile: File, startTime: number, endTime: number) => Promise<Blob>;
    concatenateVideos: (
        clips: VideoClip[],
        // transitions: Transition[],
        filter: Filter,
        musicFile: File | null,
        onProgress?: ProgressCallback
    ) => Promise<Blob>;
    extractAudio: (videoFile: File) => Promise<Blob>;
    downloadVideo: (blob: Blob, filename?: string) => void;
}

/**
 * Custom hook for FFmpeg operations
 */
export function useFFmpeg(): UseFFmpegReturn {
    const [isLoaded, setIsLoaded] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const mountedRef = useRef(true);

    // Check if FFmpeg is already loaded on mount
    useEffect(() => {
        if (ffmpegProcessor.isLoaded()) {
            setIsLoaded(true);
        }

        return () => {
            mountedRef.current = false;
        };
    }, []);

    /**
     * Load FFmpeg
     */
    const load = useCallback(async () => {
        if (isLoaded || isLoading) return;

        setIsLoading(true);
        setError(null);

        try {
            await ffmpegProcessor.load();
            if (mountedRef.current) {
                setIsLoaded(true);
            }
        } catch (err) {
            if (mountedRef.current) {
                setError(err instanceof Error ? err.message : 'Failed to load FFmpeg');
            }
            throw err;
        } finally {
            if (mountedRef.current) {
                setIsLoading(false);
            }
        }
    }, [isLoaded, isLoading]);

    /**
     * Trim video
     */
    const trimVideo = useCallback(
        async (videoFile: File, startTime: number, endTime: number): Promise<Blob> => {
            setError(null);
            try {
                return await ffmpegProcessor.trimVideo(videoFile, startTime, endTime);
            } catch (err) {
                const errorMessage = err instanceof Error ? err.message : 'Failed to trim video';
                setError(errorMessage);
                throw err;
            }
        },
        []
    );

    /**
     * Concatenate videos
     */
    const concatenateVideos = useCallback(
        async (
            clips: VideoClip[],
            // transitions: Transition[],
            filter: Filter,
            musicFile: File | null = null,
            onProgress?: ProgressCallback
        ): Promise<Blob> => {
            setError(null);
            try {
                const videoBlob = await ffmpegProcessor.concatenateVideos(
                    clips,
                    filter,
                    musicFile,
                    onProgress
                );
                return videoBlob;
            } catch (err) {
                const errorMessage = err instanceof Error ? err.message : 'Failed to concatenate videos';
                setError(errorMessage);
                throw err;
            }
        },
        []
    );

    /**
     * Extract audio from video
     */
    const extractAudio = useCallback(async (videoFile: File): Promise<Blob> => {
        setError(null);
        try {
            return await ffmpegProcessor.extractAudio(videoFile);
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Failed to extract audio';
            setError(errorMessage);
            throw err;
        }
    }, []);

    /**
     * Download video
     */
    const downloadVideo = useCallback((blob: Blob, filename?: string) => {
        ffmpegProcessor.downloadVideo(blob, filename);
    }, []);

    return {
        isLoaded,
        isLoading,
        error,
        load,
        trimVideo,
        concatenateVideos,
        extractAudio,
        downloadVideo,
    };
}
